import { useEffect, useRef, useState } from "react";
import type { VoiceNote } from "@coaching/types";
import { ApiError } from "../api";
import { clock } from "../format";
import { Icon } from "./icons";

type State =
    | { kind: "idle" }
    | { kind: "asking" }
    | { kind: "recording" }
    | { kind: "saving" }
    | { kind: "error"; message: string };

/** The best recording format this browser offers; the server re-encodes whatever comes. */
function recorderType(): string {
    const options = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm", "audio/ogg;codecs=opus"];
    return options.find((t) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) ?? "";
}

function toBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = () => reject(r.error ?? new Error("read failed"));
        r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
        r.readAsDataURL(blob);
    });
}

/**
 * Record a spoken note: a level meter and a running clock while recording,
 * stopping by itself at the limit, then saved to the server, which returns the
 * stored note. An existing note plays back here, and can be replaced or removed.
 *
 * `onRecording` reports when recording starts (its epoch ms) and stops, so the
 * studio can time drawings made meanwhile. `compact` is the one-row form that
 * sits in the toolbar under the frame.
 */
export function VoiceRecorder({ voice, url, maxSec, disabled, upload, onChange, label, recordLabel = "Record voice note", compact = false, onStart, onRecording }: {
    voice: VoiceNote | null;
    /** Where the stored note plays from, or null while the signature is loading. */
    url: string | null;
    maxSec: number;
    disabled: boolean;
    upload: (audioBase64: string) => Promise<VoiceNote>;
    onChange: (v: VoiceNote | null) => void;
    label?: string;
    recordLabel?: string;
    compact?: boolean;
    /** Called as the coach presses record, before the microphone is asked for. */
    onStart?: () => void;
    onRecording?: (startedAt: number | null) => void;
}) {
    const [state, setState] = useState<State>({ kind: "idle" });
    const [elapsed, setElapsed] = useState(0);
    const [level, setLevel] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [played, setPlayed] = useState(0);
    const recorder = useRef<MediaRecorder | null>(null);
    const stream = useRef<MediaStream | null>(null);
    const audioCtx = useRef<AudioContext | null>(null);
    const frame = useRef<number | null>(null);
    const audio = useRef<HTMLAudioElement | null>(null);

    const release = () => {
        if (frame.current !== null) cancelAnimationFrame(frame.current);
        frame.current = null;
        stream.current?.getTracks().forEach((t) => t.stop());
        stream.current = null;
        const ctx = audioCtx.current;
        audioCtx.current = null;
        ctx?.close().catch((err: unknown) => console.warn("voice meter close failed", { message: (err as Error)?.message }));
    };
    // Leaving mid-recording stops it and lets the microphone go.
    useEffect(() => () => {
        if (recorder.current?.state === "recording") recorder.current.stop();
        release();
    }, []);

    const save = async (blob: Blob) => {
        setState({ kind: "saving" });
        try {
            const note = await upload(await toBase64(blob));
            onChange(note);
            setState({ kind: "idle" });
        } catch (err) {
            setState({ kind: "error", message: err instanceof ApiError ? err.message : "The voice note couldn't be saved. Check your connection and record it again." });
        }
    };

    const start = async () => {
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            setState({ kind: "error", message: "This browser can't record audio. Try Chrome, Edge or Safari." });
            return;
        }
        audio.current?.pause();
        onStart?.();
        setState({ kind: "asking" });
        try {
            stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        } catch (err) {
            const denied = (err as DOMException)?.name === "NotAllowedError";
            setState({ kind: "error", message: denied ? "Allow microphone access in your browser's address bar, then try again." : "No microphone was found. Plug one in and try again." });
            return;
        }
        const type = recorderType();
        const rec = new MediaRecorder(stream.current, type ? { mimeType: type } : undefined);
        const chunks: Blob[] = [];
        rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        rec.onstop = () => { release(); onRecording?.(null); void save(new Blob(chunks, { type: rec.mimeType || type })); };
        recorder.current = rec;

        // A live level, so the coach can see the microphone is hearing them.
        const ctx = new AudioContext();
        audioCtx.current = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream.current).connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        const startedAt = Date.now();
        const tick = () => {
            analyser.getByteTimeDomainData(buf);
            let peak = 0;
            for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
            setLevel(Math.min(1, peak / 64));
            const sec = (Date.now() - startedAt) / 1000;
            setElapsed(sec);
            if (sec >= maxSec && rec.state === "recording") rec.stop();
            frame.current = requestAnimationFrame(tick);
        };
        rec.start(250);
        setElapsed(0);
        setState({ kind: "recording" });
        onRecording?.(startedAt);
        frame.current = requestAnimationFrame(tick);
    };

    const stop = () => { if (recorder.current?.state === "recording") recorder.current.stop(); };

    const togglePlay = () => {
        const a = audio.current;
        if (!a) return;
        if (a.paused) a.play().catch(() => setState({ kind: "error", message: "The voice note couldn't play. Reload the page and try again." }));
        else a.pause();
    };

    const recording = state.kind === "recording";
    const body = (
        <>
            {recording && (
                <div className="voice-row">
                    <button type="button" className="voice-btn stop" onClick={stop} aria-label="Stop recording" title="Stop recording"><Icon.stop /></button>
                    <span className="voice-time"><span className="rec-dot" aria-hidden />{clock(elapsed)}</span>
                    <div className="meter" aria-hidden><span style={{ transform: `scaleX(${0.08 + level * 0.92})` }} /></div>
                    {compact && <span className="voice-tip">Draw as you talk</span>}
                </div>
            )}

            {state.kind === "saving" && <div className="voice-row voice-saving"><span className="spinner small" aria-hidden />Saving voice note</div>}

            {!recording && state.kind !== "saving" && voice && (
                <div className="voice-row">
                    <button type="button" className="voice-btn" onClick={togglePlay} disabled={!url} aria-label={playing ? "Pause voice note" : "Play voice note"} title="Play voice note">
                        {playing ? <Icon.pause /> : <Icon.play />}
                    </button>
                    <div className="voice-progress" aria-hidden><span style={{ width: `${Math.min(100, (played / Math.max(voice.durationSec, 0.1)) * 100)}%` }} /></div>
                    <span className="voice-time">{clock(voice.durationSec)}</span>
                    {!disabled && (
                        <>
                            <button type="button" className="icon-btn" onClick={() => void start()} aria-label="Record again" title="Record again"><Icon.mic /></button>
                            <button type="button" className="icon-btn danger" onClick={() => onChange(null)} aria-label="Remove voice note" title="Remove voice note"><Icon.trash /></button>
                        </>
                    )}
                    {url && <audio ref={audio} src={url} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                        onEnded={() => { setPlaying(false); setPlayed(0); }} onTimeUpdate={(e) => setPlayed(e.currentTarget.currentTime)} />}
                </div>
            )}

            {!recording && state.kind !== "saving" && !voice && !disabled && (
                <button type="button" className="btn record" onClick={() => void start()} disabled={state.kind === "asking"}>
                    <span className="rec-dot" aria-hidden />{state.kind === "asking" ? "Waiting for the microphone" : recordLabel}
                </button>
            )}
            {!voice && disabled && state.kind === "idle" && !compact && <p className="muted small voice-none">No voice note.</p>}
            {state.kind === "error" && <p className="error-text small voice-error" role="alert">{state.message}</p>}
        </>
    );

    if (compact) return <div className={`voice compact${recording ? " live" : ""}`}>{body}</div>;
    return (
        <div className={`voice${recording ? " live" : ""}`}>
            <div className="voice-head">
                <span className="voice-label"><Icon.mic />{label}</span>
                <span className="muted small">Up to {clock(maxSec)}</span>
            </div>
            {body}
        </div>
    );
}
